import type React from 'react';
import { ReactComponent as DocumentIcon } from 'src/assets/tailwind/Notifications/document-icon.svg';
import { ReactComponent as ContentShareIcon } from 'src/assets/tailwind/Notifications/content-sharing-icon.svg';
import { ReactComponent as AssistantIcon } from 'src/assets/tailwind/Notifications/assistants-icon.svg';
import { ReactComponent as WorkspaceIcon } from 'src/assets/tailwind/Notifications/workspace-icon.svg';
import { ReactComponent as ConnectorSyncIcon } from 'src/assets/tailwind/Notifications/connector-icon.svg';
import { ReactComponent as AnnouncementIcon } from 'src/assets/tailwind/Notifications/announcement-icon.svg';
import { ReactComponent as TenantIcon } from 'src/assets/tailwind/Notifications/tenant-icon.svg';
import { ReactComponent as WorkflowIcon } from 'src/assets/tailwind/Notifications/workflow-icon.svg';
import { ReactComponent as BillingIcon } from 'src/assets/tailwind/Notifications/subscription-icon.svg';
import { ReactComponent as UserRole } from 'src/assets/tailwind/Notifications/user-role-icon.svg';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import { NotificationCategory } from './enum';

export const IMPLEMENTED_NOTIFICATION_CATEGORIES = new Set<string>([
  NotificationCategory.DOCUMENT_PROCESSING,
  NotificationCategory.TENANT_LIFECYCLE
]);

export const isCategoryImplemented = (categoryName: string): boolean =>
  IMPLEMENTED_NOTIFICATION_CATEGORIES.has(categoryName);

export const PAGE_SIZE = 20;

export const NOTIFICATION_RETENTION_DAYS = 90;

export const NOTIFICATIONS_PANEL_OFFSET = 18;

export const categoryIconMap: Record<string, React.FC<any>> = {
  [NotificationCategory.DOCUMENT_PROCESSING]: DocumentIcon,
  [NotificationCategory.CONTENT_SHARING]: ContentShareIcon,
  [NotificationCategory.ASSISTANT_UPDATES]: AssistantIcon,
  [NotificationCategory.WORKSPACE_CHANGES]: WorkspaceIcon,
  [NotificationCategory.CONNECTOR_SYNC_STATUS]: ConnectorSyncIcon,
  [NotificationCategory.SYSTEM_ANNOUNCEMENTS]: AnnouncementIcon,
  [NotificationCategory.WORKFLOW_EVENTS]: WorkflowIcon,
  [NotificationCategory.TENANT_LIFECYCLE]: TenantIcon,
  [NotificationCategory.SUBSCRIPTION_BILLING]: BillingIcon,
  [NotificationCategory.USER_ROLES]: UserRole,
  [NotificationCategory.DOC_ANALYST]: DocumentIcon,
};


export const categoryKeyMap: Record<string, string> = {
  'Document Processing': T.notifCatDocumentProcessing,
  'Content Sharing': T.notifCatContentSharing,
  'Assistant Updates': T.notifCatAssistantUpdates,
  'Workspace Changes': T.notifCatWorkspaceChanges,
  'Connector Sync Status': T.notifCatConnectorSyncStatus,
  'System Announcements': T.notifCatSystemAnnouncements,
  'Workflow Events': T.notifCatWorkflowEvents,
  'Tenant Lifecycle': T.notifCatTenantLifecycle,
  'Subscription and Billing': T.notifCatSubscriptionAndBilling,
  'User and Roles': T.notifCatUserAndRoles,
  'Doc Analyst': T.notifCatDocAnalyst,
};

export const categoryDescriptionKeyMap: Record<string, string> = {
  'Document Processing': T.notifCatDescDocumentProcessing,
  'Content Sharing': T.notifCatDescContentSharing,
  'Assistant Updates': T.notifCatDescAssistantUpdates,
  'Workspace Changes': T.notifCatDescWorkspaceChanges,
  'Connector Sync Status': T.notifCatDescConnectorSyncStatus,
  'System Announcements': T.notifCatDescSystemAnnouncements,
  'Workflow Events': T.notifCatDescWorkflowEvents,
  'Tenant Lifecycle': T.notifCatDescTenantLifecycle,
  'Subscription and Billing': T.notifCatDescSubscriptionAndBilling,
  'User and Roles': T.notifCatDescUserAndRoles,
  'Doc Analyst': T.notifCatDescDocAnalyst,
};

export const subCategoryDescriptionKeyMap: Record<string, string> = {
  'Document processed': T.notifSubCatDescDocumentProcessed,
  'Document failed': T.notifSubCatDescDocumentFailed,
  'Batch upload complete': T.notifSubCatDescBatchUploadComplete,
  'Document shared': T.notifSubCatDescDocumentShared,
  'Access changed': T.notifSubCatDescAccessChanged,
  'Connector sync complete': T.notifSubCatDescConnectorSyncComplete,
  'Connector sync failed': T.notifSubCatDescConnectorSyncFailed,
  'Tenant setting changed': T.notifSubCatDescTenantSettingChanged,
  'Subscription updated': T.notifSubCatDescSubscriptionUpdated,
  'Subscription Expiry Reminder': T.notifSubCatDescSubscriptionExpiryReminder,
  'Account type changed': T.notifSubCatDescAccountTypeChanged,
  'Usage limit warning': T.notifSubCatDescUsageLimitWarning,
  'System maintenance': T.notifSubCatDescSystemMaintenance,
  'Model added': T.notifSubCatDescModelAdded,
  'Model deprecated': T.notifSubCatDescModelDeprecated,
  'Weekly digest': T.notifSubCatDescWeeklyDigest,
  'New tenant created': T.notifSubCatDescNewTenantCreated,
  'Tenant deactivation warn': T.notifSubCatDescTenantDeactivationWarn,
  'Assistant Shared': T.notifSubCatDescAssistantShared,
  'Assistant Updated': T.notifSubCatDescAssistantUpdated,
  'Assistant Removed': T.notifSubCatDescAssistantRemoved,
  'Analysis Completed': T.notifSubCatDescAnalysisCompleted,
  'Analysis Partial': T.notifSubCatDescAnalysisPartial,
  'Analysis Failed': T.notifSubCatDescAnalysisFailed,
  'Dataset Modified': T.notifSubCatDescDatasetModified,
  'Workspace Deleted': T.notifSubCatDescWorkspaceDeleted,
  'Workspace Created': T.notifSubCatDescWorkspaceCreated,
  'Workspace Assigned': T.notifSubCatDescWorkspaceAssigned,
  'Role Changed': T.notifSubCatDescRoleChanged,
  'User Added': T.notifSubCatDescUserAdded,
  'User Removed': T.notifSubCatDescUserRemoved,
  'Workflow Completed': T.notifSubCatDescWorkflowCompleted,
  'Workflow Failed': T.notifSubCatDescWorkflowFailed,
  'Approval Required': T.notifSubCatDescApprovalRequired,
  'Approval Reminder': T.notifSubCatDescApprovalReminder,
};

export const subCategorySummaryKeyMap: Record<string, string> = {
  'Document processed': T.notifSummaryDocumentProcessed,
  'Document failed': T.notifSummaryDocumentFailed,
  'Batch upload complete': T.notifSummaryBatchUploadComplete,
  'Document shared': T.notifSummaryDocumentShared,
  'Access changed': T.notifSummaryAccessChanged,
  'Connector sync complete': T.notifSummaryConnectorSyncComplete,
  'Connector sync failed': T.notifSummaryConnectorSyncFailed,
  'Tenant setting changed': T.notifSummaryTenantSettingChanged,
  'Subscription updated': T.notifSummarySubscriptionUpdated,
  'Subscription Expiry Reminder': T.notifSummarySubscriptionExpiryReminder,
  'Account type changed': T.notifSummaryAccountTypeChanged,
  'Usage limit warning': T.notifSummaryUsageLimitWarning,
  'System maintenance': T.notifSummarySystemMaintenance,
  'Model added': T.notifSummaryModelAdded,
  'Model deprecated': T.notifSummaryModelDeprecated,
  'Weekly digest': T.notifSummaryWeeklyDigest,
  'New tenant created': T.notifSummaryNewTenantCreated,
  'Tenant deactivation warn': T.notifSummaryTenantDeactivationWarn,
  'Assistant Shared': T.notifSummaryAssistantShared,
  'Assistant Updated': T.notifSummaryAssistantUpdated,
  'Assistant Removed': T.notifSummaryAssistantRemoved,
  'Analysis Completed': T.notifSummaryAnalysisCompleted,
  'Analysis Partial': T.notifSummaryAnalysisPartial,
  'Analysis Failed': T.notifSummaryAnalysisFailed,
  'Dataset Modified': T.notifSummaryDatasetModified,
  'Workspace Deleted': T.notifSummaryWorkspaceDeleted,
  'Workspace Created': T.notifSummaryWorkspaceCreated,
  'Workspace Assigned': T.notifSummaryWorkspaceAssigned,
  'Role Changed': T.notifSummaryRoleChanged,
  'User Added': T.notifSummaryUserAdded,
  'User Removed': T.notifSummaryUserRemoved,
  'Workflow Completed': T.notifSummaryWorkflowCompleted,
  'Workflow Failed': T.notifSummaryWorkflowFailed,
  'Approval Required': T.notifSummaryApprovalRequired,
  'Approval Reminder': T.notifSummaryApprovalReminder,
};

export const subCategoryKeyMap: Record<string, string> = {
  'Document processed': T.notifSubCatDocumentProcessed,
  'Document failed': T.notifSubCatDocumentFailed,
  'Batch upload complete': T.notifSubCatBatchUploadComplete,
  'Document shared': T.notifSubCatDocumentShared,
  'Access changed': T.notifSubCatAccessChanged,
  'Connector sync complete': T.notifSubCatConnectorSyncComplete,
  'Connector sync failed': T.notifSubCatConnectorSyncFailed,
  'Tenant setting changed': T.notifSubCatTenantSettingChanged,
  'Subscription updated': T.notifSubCatSubscriptionUpdated,
  'Subscription Expiry Reminder': T.notifSubCatSubscriptionExpiryReminder,
  'Account type changed': T.notifSubCatAccountTypeChanged,
  'Usage limit warning': T.notifSubCatUsageLimitWarning,
  'System maintenance': T.notifSubCatSystemMaintenance,
  'Model added': T.notifSubCatModelAdded,
  'Model deprecated': T.notifSubCatModelDeprecated,
  'Weekly digest': T.notifSubCatWeeklyDigest,
  'New tenant created': T.notifSubCatNewTenantCreated,
  'Tenant deactivation warn': T.notifSubCatTenantDeactivationWarn,
  'Assistant Shared': T.notifSubCatAssistantShared,
  'Assistant Updated': T.notifSubCatAssistantUpdated,
  'Assistant Removed': T.notifSubCatAssistantRemoved,
  'Analysis Completed': T.notifSubCatAnalysisCompleted,
  'Analysis Partial': T.notifSubCatAnalysisPartial,
  'Analysis Failed': T.notifSubCatAnalysisFailed,
  'Dataset Modified': T.notifSubCatDatasetModified,
  'Workspace Deleted': T.notifSubCatWorkspaceDeleted,
  'Workspace Created': T.notifSubCatWorkspaceCreated,
  'Workspace Assigned': T.notifSubCatWorkspaceAssigned,
  'Role Changed': T.notifSubCatRoleChanged,
  'User Added': T.notifSubCatUserAdded,
  'User Removed': T.notifSubCatUserRemoved,
  'Workflow Completed': T.notifSubCatWorkflowCompleted,
  'Workflow Failed': T.notifSubCatWorkflowFailed,
  'Approval Required': T.notifSubCatApprovalRequired,
  'Approval Reminder': T.notifSubCatApprovalReminder,
};

export const ADMIN_CONSOLE_NOTIFICATIONS_ROUTE = "/admin-console/notifications";
export const SETTINGS_NOTIFICATIONS_ROUTE = "/settings/notifications";